import { useQuery } from '@tanstack/react-query';
import { fetchApi } from './client';

/**
 * Productivity — trimester review-completion tracking (supervision revamp Task 10).
 *
 * Backed by `GET /api/productivity/trimesters?year=YYYY`. Strictly supervision-review
 * metrics (distinct from the Task Log / task-completion feature). A review is attributed
 * to the trimester containing its periodEnd; the denominator is the current non-excluded
 * bookkeeping-client roster.
 */

export type TrimesterKey = 'T1' | 'T2' | 'T3';

export interface TrimesterRange {
  start: string; // yyyy-MM-dd
  end: string; // yyyy-MM-dd
}

/**
 * Client-months in one trimester (productivity spec section 2). The four buckets partition `total`;
 * "565 of 924 months" is `covered` of `total`.
 */
export interface MonthTally {
  /** A completed review covers the month. */
  covered: number;
  /** An in-progress review covers it. */
  inProgress: number;
  /** A pending review covers it. */
  pending: number;
  /** No non-cancelled review covers it. */
  uncovered: number;
  total: number;
}

export interface TrimesterBucket {
  key: TrimesterKey;
  range: TrimesterRange;
  /** Clients FULLY complete: every in-window month covered by a completed review (since 2026-10-20). */
  completed: number;
  inProgress: number;
  pending: number;
  notStarted: number;
  /** Clients in scope this trimester. */
  total: number;
  /** The primary figure. Absent only from an API older than the client-month rework. */
  months?: MonthTally;
  /** What is behind each month segment (spec §5). Absent from an API older than the drill-through. */
  drill?: MonthDrill;
}

/**
 * The reviews that give a segment its months, and the clients with a gray month. A drill-through
 * shows exactly these rows, so the segment's count and the target's rows agree by construction.
 */
export interface MonthDrill {
  covered: string[];
  inProgress: string[];
  pending: string[];
  /** Client (account) ids with at least one gray in-window month. */
  uncoveredClients: string[];
}

export interface SupervisorTrimesters {
  staffId: string;
  name: string;
  trimesters: TrimesterBucket[];
}

export interface RecentPace {
  week: number;
  month: number;
  last30: number;
}

export interface ProductivityTrimesters {
  year: number;
  trimesters: TrimesterBucket[];
  bySupervisor: SupervisorTrimesters[];
  recent: RecentPace;
}

export function getProductivityTrimesters(year: number): Promise<ProductivityTrimesters> {
  return fetchApi<ProductivityTrimesters>(`/productivity/trimesters?year=${year}`);
}

export const PRODUCTIVITY_TRIMESTERS_QUERY_KEY = 'productivityTrimesters';

export function useProductivityTrimesters(year: number, options: { enabled?: boolean } = {}) {
  return useQuery<ProductivityTrimesters>({
    queryKey: [PRODUCTIVITY_TRIMESTERS_QUERY_KEY, year],
    queryFn: () => getProductivityTrimesters(year),
    enabled: options.enabled ?? true,
  });
}
